import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { tradingAccounts, type TradingAccount } from "@/api/tradingAccounts";
import { useAuth } from "@/auth/AuthContext";

const STORAGE_KEY = "piggy.active_account_id";

interface Ctx {
  accounts: TradingAccount[];
  active: TradingAccount | null;
  setActiveId: (id: string | null) => void;
  refresh: () => Promise<unknown>;
  isLoading: boolean;
}

const Ctx = createContext<Ctx | null>(null);

/**
 * Workspace-state for the currently-selected trading account. Phase 9
 * pivots the platform to a broker model where every order is for a
 * specific trading_account; the account picker lives in the navbar and
 * the choice persists per-tab in sessionStorage.
 */
export function ActiveAccountProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [activeId, _setActiveId] = useState<string | null>(() => {
    return sessionStorage.getItem(STORAGE_KEY);
  });

  const list = useQuery({
    queryKey: ["trading-accounts", "mine"],
    queryFn: () => tradingAccounts.list(true),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  // Auto-pick the first active account if nothing is selected yet.
  useEffect(() => {
    if (!list.data || list.data.length === 0) return;
    if (activeId && list.data.some((a) => a.id === activeId)) return;
    const first = list.data.find((a) => a.status === "active") ?? list.data[0];
    if (first) {
      sessionStorage.setItem(STORAGE_KEY, first.id);
      _setActiveId(first.id);
    }
  }, [list.data, activeId]);

  function setActiveId(id: string | null) {
    if (id) sessionStorage.setItem(STORAGE_KEY, id);
    else sessionStorage.removeItem(STORAGE_KEY);
    _setActiveId(id);
  }

  const active = useMemo(
    () => list.data?.find((a) => a.id === activeId) ?? null,
    [list.data, activeId],
  );

  const value: Ctx = {
    accounts: list.data ?? [],
    active,
    setActiveId,
    refresh: () => list.refetch(),
    isLoading: list.isLoading,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useActiveAccount(): Ctx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useActiveAccount used outside ActiveAccountProvider");
  return v;
}

/** Compact account-switcher dropdown rendered in the navbar. */
export function ActiveAccountSwitcher() {
  const { accounts, active, setActiveId, isLoading } = useActiveAccount();
  if (isLoading || accounts.length === 0) return null;
  return (
    <select
      value={active?.id ?? ""}
      onChange={(e) => setActiveId(e.target.value || null)}
      className="rounded-sm border border-term-border bg-term-bg px-2 py-1 text-2xs text-term-fg focus:border-brand focus:outline-none"
      title="Active trading account"
    >
      {accounts.map((a) => (
        <option key={a.id} value={a.id}>
          {a.login_code}
          {a.label ? ` · ${a.label}` : ""}
        </option>
      ))}
    </select>
  );
}
